import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, Send, Loader2, X, Database } from "lucide-react";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAIContext } from "@/context/AIContextProvider";

// Map route → module code and appropriate AI service
const ROUTE_TO_SERVICE = {
  "/":           { module: "dashboard",  service: "assistant" },
  "/finance":    { module: "finance",    service: "financial-insights" },
  "/performance":{ module: "performance", service: "financial-insights" },
  "/assets":     { module: "assets",     service: "asset-intelligence" },
  "/vehicles":   { module: "vehicles",   service: "assistant" },
  "/hr":         { module: "hr",         service: "assistant" },
  "/spreadsheets":{module: "documents",  service: "document-analysis" },
  "/crm":        { module: "crm",        service: "assistant" },
  "/audit":      { module: "audit",      service: "assistant" },
  "/hseq":       { module: "hseq",       service: "assistant" },
  "/org":        { module: "org_mgmt",   service: "assistant" },
  "/search":     { module: "dashboard",  service: "search" },
};

export function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const { pathname } = useLocation();
  const { context } = useAIContext();

  // Match longest prefix
  const key = Object.keys(ROUTE_TO_SERVICE)
    .filter((k) => pathname === k || pathname.startsWith(k + "/"))
    .sort((a, b) => b.length - a.length)[0] || "/";
  const { module, service } = ROUTE_TO_SERVICE[key];

  const dataAware = !!context && (context.module === module || !context.module);

  const send = async () => {
    const text = prompt.trim();
    if (!text) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setPrompt("");
    setBusy(true);
    try {
      const payload = { prompt: text, module };
      if (dataAware) payload.context = context;
      // AI calls (Claude Sonnet with page context) can legitimately take 30-60s.
      // Override axios' default 20s timeout so long responses don't get aborted client-side.
      const { data } = await api.post(`/ai/${service}`, payload, { timeout: 90000 });
      setMessages((m) => [...m, { role: "ai", text: data.response, service: data.service }]);
    } catch (e) {
      const isTimeout = e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "");
      const detail = e?.response?.data?.detail;
      const msg = isTimeout
        ? "The AI is taking longer than expected. Please try again, or ask a shorter question."
        : (typeof detail === "string" && detail) || "Sorry, I couldn't reach the AI service. Please try again.";
      setMessages((m) => [...m, { role: "ai", text: msg, error: true }]);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        data-testid={TID.aiOpen}
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 w-12 h-12 rounded-full bg-gbe text-gbe-accent shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center z-50"
        aria-label="Open AI Assistant"
      >
        <Sparkles className="w-5 h-5" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 w-[380px] max-w-[calc(100vw-40px)] bg-white border border-slate-200 rounded-lg shadow-2xl z-50 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 bg-gbe text-white">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Sparkles className="w-4 h-4 text-gbe-accent" />
          AI Assistant
          <span className="text-[10px] uppercase text-white/60">· {service}</span>
          {dataAware && (
            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-200" title="Assistant is seeing the current page's data">
              <Database className="w-2.5 h-2.5" /> data-aware
            </span>
          )}
        </div>
        <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="h-72 overflow-y-auto px-3 py-3 bg-slate-50">
        {messages.length === 0 && (
          <div className="text-xs text-slate-500 text-center mt-24">
            Ask about your data — this assistant is context-aware for the <b className="text-gbe">{module}</b> module.
            {dataAware && <div className="mt-1 text-emerald-600">Live data from this page is attached to your questions.</div>}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`mb-3 flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
              m.role === "user"
                ? "bg-gbe text-white"
                : m.error
                  ? "bg-rose-50 border border-rose-200 text-rose-700"
                  : "bg-white border border-slate-200 text-slate-700"
            }`}>
              {m.text}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start mb-3">
            <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-500">
              <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-2" /> Thinking…
            </div>
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="flex items-center gap-2 px-3 py-2 border-t border-slate-200 bg-white"
      >
        <Input
          data-testid={TID.aiInput}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Ask about your data…"
          disabled={busy}
          className="h-9 text-sm"
        />
        <Button
          data-testid={TID.aiSend}
          type="submit"
          disabled={busy || !prompt.trim()}
          size="sm"
          className="bg-gbe hover:bg-gbe/90 text-white"
        >
          <Send className="w-3.5 h-3.5" />
        </Button>
      </form>
    </div>
  );
}
